import {
    createEntityAdapter,
    createSelector,
    createSlice,
    EntityState,
} from "@reduxjs/toolkit";

import { RootState } from "../../../app/store";
import {
    SanctionEvent,
    SanctionEventEntity,
    SanctionEventMetadata,
} from "../../../api/sanctionEvents";
import { RequestStatus } from "../../legalTechnicalAnalysis/Logic/assetsSlice";
import {
    fetchSanctionEvents,
    upsertSanctionEvents,
} from "./sanctionEventsThunk";

export const sanctionEventsMetadataAdapter = createEntityAdapter<
    SanctionEventMetadata,
    string
>({
    selectId: (event) => event.event_id,
});

export const sanctionEventsDetailsAdapter = createEntityAdapter<
    SanctionEventEntity,
    string
>({
    selectId: (detail) => detail.detail_id,
});

export interface SanctionEventsState {
    eventsMetadata: EntityState<SanctionEventMetadata, string>;
    eventsDetails: EntityState<SanctionEventEntity, string>;
    status: RequestStatus;
    error?: string | null;
    caseId?: string;
}

type RootStateWithSanctionEvents = RootState & {
    sanctionEvents: SanctionEventsState;
};

const initialState: SanctionEventsState = {
    eventsMetadata: sanctionEventsMetadataAdapter.getInitialState(),
    eventsDetails: sanctionEventsDetailsAdapter.getInitialState(),
    status: "idle",
    error: null,
    caseId: undefined,
};

const sanctionEventsSlice = createSlice({
    name: "sanctionEvents",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
      builder
        .addCase(fetchSanctionEvents.pending, (state) => {
          state.status = "loading";
          state.error = null;
          sanctionEventsMetadataAdapter.removeAll(state.eventsMetadata);
          sanctionEventsDetailsAdapter.removeAll(state.eventsDetails);
        })
        .addCase(fetchSanctionEvents.fulfilled, (state, action) => {
          state.status = "success";
          state.caseId = action.meta.arg;
          state.error = null;

          sanctionEventsMetadataAdapter.setAll(
            state.eventsMetadata,
            action.payload.events_metadata,
          );
          sanctionEventsDetailsAdapter.setAll(
            state.eventsDetails,
            action.payload.events_details,
          );
        })
        .addCase(fetchSanctionEvents.rejected, (state) => {
          state.status = "failed";
          state.error = "Failed to fetch sanction events";
        })
        .addCase(upsertSanctionEvents.fulfilled, (state, action) => {
          sanctionEventsMetadataAdapter.upsertMany(
            state.eventsMetadata,
            action.meta.arg.request.events_metadata ?? [],
          );
          sanctionEventsDetailsAdapter.upsertMany(
            state.eventsDetails,
            action.meta.arg.request.events_details ?? [],
          );
        });
    },
});

export const sanctionEventsReducer = sanctionEventsSlice.reducer;

const sanctionEventsMetadataSelectors =
    sanctionEventsMetadataAdapter.getSelectors<RootStateWithSanctionEvents>(
      (state) => state.sanctionEvents.eventsMetadata,
);

const sanctionEventsDetailsSelectors =
    sanctionEventsDetailsAdapter.getSelectors<RootStateWithSanctionEvents>(
      (state) => state.sanctionEvents.eventsDetails,
);

export const {
    selectAll: selectAllSanctionEventMetadata,
    selectById: selectSanctionEventMetadataById,
    selectEntities: selectSanctionEventMetadataEntities,
    selectIds: selectSanctionEventMetadataIds,
    selectTotal: selectSanctionEventMetadataCount,
} = sanctionEventsMetadataSelectors;

export const {
    selectAll: selectAllSanctionEventDetails,
    selectById: selectSanctionEventDetailById,
    selectEntities: selectSanctionEventDetailEntities,
    selectIds: selectSanctionEventDetailIds,
    selectTotal: selectSanctionEventDetailCount,
} = sanctionEventsDetailsSelectors;

export const selectSanctionEventsStatus = (
    state: RootStateWithSanctionEvents,
) => state.sanctionEvents.status;

export const selectSanctionEventsError = (
    state: RootStateWithSanctionEvents,
) => state.sanctionEvents.error;

export const selectSanctionEventsCaseId = (
    state: RootStateWithSanctionEvents,
) => state.sanctionEvents.caseId;

export const selectSanctionEventDetailsByEventId = createSelector(
    [selectAllSanctionEventDetails],
    (details) =>
      details.reduce<Record<string, SanctionEventEntity[]>>((acc, detail) => {
        if (!acc[detail.event_id]) {
          acc[detail.event_id] = [];
        }

        acc[detail.event_id].push(detail);
        return acc;
      }, {}),
);

export const selectSanctionEventsSortedByDate = createSelector(
    [selectAllSanctionEventMetadata, selectSanctionEventDetailsByEventId],
    (eventsMetadata, detailsByEventId): SanctionEvent[] =>
      [...eventsMetadata]
        .sort((a, b) => a.sanction_date.localeCompare(b.sanction_date))
        .map((metadata) => ({
          ...metadata,
          details: detailsByEventId[metadata.event_id] ?? [],
        })),
);

export const selectSanctionEventTableRows = createSelector(
    [selectSanctionEventsSortedByDate],
    (sanctionEvents) => sanctionEvents,
);
